import { useEffect, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "./use-store"
import {
  setSelectedBrandID,
  clearSelectedBrandID,
  selectSelectedBrandID,
  selectMergedThreads,
  setMergedThreads,
  setBrandOptions
} from "../../redux/slices/conversations"
import { useGetThreadsQuery } from "../../redux/queries"
import { useUserLastActivity } from "./use-user-last-activity"
import { GetThreadsParams } from "../../types"
import { useUserRoles } from "./use-user-roles"
import { generateBrandOptions, mergeThreads } from "../utils"

export const useConversations = () => {
  const dispatch = useAppDispatch()
  const lastActivity = useUserLastActivity()
  const selectedBrandID = useAppSelector(selectSelectedBrandID)
  const mergedThreads = useAppSelector(selectMergedThreads)
  const { isBankAdmin, isSuperAdmin, user } = useUserRoles()

  const [isLoadingQueryChange, setIsLoadingQueryChange] =
    useState<boolean>(true)
  const [playNewMessageSound, setPlayNewMessageSound] = useState<boolean>(false)

  // Store the last timestamp for API calls
  const lastCallTimestamp = useRef<number>()

  // Handle setting or clearing selectedBrandID based on user roles
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(clearSelectedBrandID()) // Clear brand selection for super admins
    } else {
      dispatch(setSelectedBrandID(Number(user?.brand_id))) // Set brand for bank admins
    }
  }, [dispatch, isSuperAdmin, user?.brand_id])

  useEffect(() => {
    setIsLoadingQueryChange(true)
    lastCallTimestamp.current = undefined
  }, [selectedBrandID])

  // TODO: queryParam is updated before the useEffect above. We need to address this.
  let queryParam: GetThreadsParams = {}
  let query: GetThreadsParams = {}

  // Only add `lastCallTimestamp` if it's not the first call after brand change
  if (!isLoadingQueryChange && lastCallTimestamp.current) {
    query.updated_min = lastCallTimestamp.current
  }

  if (isSuperAdmin) {
    queryParam = { brand_id: selectedBrandID ?? undefined, ...query }
  } else if (isBankAdmin) {
    queryParam = { brand_id: user?.brand_id ?? undefined, ...query }
  } else {
    queryParam = { loid: user?.id, ...query }
  }

  // Fetch threads using the query
  const {
    data: threads = [],
    error,
    isError,
    isFetching,
    isLoading,
    isSuccess
  } = useGetThreadsQuery(queryParam, {
    pollingInterval: 10000, // Poll every 10s
    skip: Date.now() - lastActivity > 180000 // Poll if activity in the last 3 mins
  })

  // Merge threads whenever there's a change in `threads` or `mergedThreads`
  useEffect(() => {
    if (threads.length > 0 || mergedThreads.length > 0) {
      // Reset flag after the first call after brand change
      if (!isFetching && isLoadingQueryChange) {
        lastCallTimestamp.current = Date.now()
        setIsLoadingQueryChange(false)

        dispatch(setMergedThreads(threads))
        return
      }

      const { merged, hasNewMessages } = mergeThreads(mergedThreads, threads)

      // Only dispatch merged threads if they have changed
      if (JSON.stringify(merged) !== JSON.stringify(mergedThreads)) {
        dispatch(setMergedThreads(merged))
      }

      // Only update playNewMessageSound if it has changed
      if (hasNewMessages !== playNewMessageSound) {
        setPlayNewMessageSound(hasNewMessages)
      }
    }
  }, [
    threads,
    mergedThreads,
    dispatch,
    playNewMessageSound,
    isFetching,
    isLoadingQueryChange
  ])

  // Generate brand options based on merged threads
  useEffect(() => {
    const brandOptions = generateBrandOptions(mergedThreads)
    dispatch(setBrandOptions(brandOptions))
  }, [mergedThreads, dispatch])

  return {
    playNewMessageSound,
    threads: mergedThreads,
    error,
    isSuccess,
    isError,
    isFetching,
    isLoading,
    isLoadingQueryChange
  }
}
